/**
 * Request-scoped auth for HTTP transport mode.
 *
 * stdio: API key from PRODUCTBRAIN_API_KEY env, one user per process.
 * http:  API key from Bearer header per request, many users per process.
 *
 * AsyncLocalStorage propagates the token through the async call chain
 * so client.ts resolves the correct API key and state per request.
 */

import { AsyncLocalStorage } from "node:async_hooks";
import { createHash } from "node:crypto";

// ── Key Hashing (Fix 3 — session binding) ───────────────────────────────

/**
 * Short one-way hash of an API key used to bind MCP sessions to a specific key.
 * Not a secret — stored in the session entry to detect session hijacking.
 */
export function hashKey(key: string): string {
  return createHash("sha256").update(key).digest("hex").slice(0, 16);
}

// ── Request Context ─────────────────────────────────────────────────────

interface RequestAuth {
  apiKey: string;
  /**
   * WP-479 review fix (Codex re-review): the HTTP transport's `Mcp-Session-Id` for this request.
   * Session-lifecycle state (the active agentSessionId + oriented/closed flags) keys on this so two
   * concurrent HTTP streams SHARING an API key don't overwrite each other's active session. Absent
   * in STDIO mode (one process = one session) and on the pre-session initialize request.
   */
  mcpSessionId?: string;
}

const requestStore = new AsyncLocalStorage<RequestAuth>();

export function runWithAuth<T>(auth: RequestAuth, fn: () => T | Promise<T>): T | Promise<T> {
  return requestStore.run(auth, fn);
}

export function getRequestApiKey(): string | undefined {
  return requestStore.getStore()?.apiKey;
}

/** The current HTTP request's `Mcp-Session-Id`, when present (HTTP transport, post-initialize). */
export function getRequestMcpSessionId(): string | undefined {
  return requestStore.getStore()?.mcpSessionId;
}

// ── Per-Key State (HTTP mode) ───────────────────────────────────────────

export interface KeyState {
  workspaceId: string | null;
  workspaceSlug: string | null;
  workspaceName: string | null;
  workspaceCreatedAt: number | null;
  /** BET-76 FEAT-111: Cached at workspace resolution time. Defaults to 'open'. */
  workspaceGovernanceMode: "open" | "consensus" | "role" | null;
  agentSessionId: string | null;
  apiKeyId: string | null;
  apiKeyScope: "read" | "readwrite";
  sessionOriented: boolean;
  sessionClosed: boolean;
  lastAccess: number;
  /** DEC-789 S2: Convex deployment URL this key belongs to, resolved at key-check time. */
  deploymentUrl: string | null;
}

const SESSION_TTL_MS = 30 * 60 * 1000;
const MAX_KEYS = 100;
const keyStateMap = new Map<string, KeyState>();

function newKeyState(): KeyState {
  return {
    workspaceId: null,
    workspaceSlug: null,
    workspaceName: null,
    workspaceCreatedAt: null,
    workspaceGovernanceMode: null,
    agentSessionId: null,
    apiKeyId: null,
    apiKeyScope: "readwrite",
    sessionOriented: false,
    sessionClosed: false,
    lastAccess: Date.now(),
    deploymentUrl: null,
  };
}

export function getKeyState(apiKey: string): KeyState {
  let s = keyStateMap.get(apiKey);
  if (!s) {
    s = newKeyState();
    keyStateMap.set(apiKey, s);
    evictStale();
  }
  s.lastAccess = Date.now();
  return s;
}

function evictStale(): void {
  if (keyStateMap.size <= MAX_KEYS) return;
  const now = Date.now();
  for (const [key, s] of keyStateMap) {
    if (now - s.lastAccess > SESSION_TTL_MS) keyStateMap.delete(key);
  }
  if (keyStateMap.size > MAX_KEYS) {
    const sorted = [...keyStateMap.entries()].sort((a, b) => a[1].lastAccess - b[1].lastAccess);
    for (let i = 0; i < sorted.length - MAX_KEYS; i++) {
      keyStateMap.delete(sorted[i][0]);
    }
  }
}
